import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { CONFIG } from "../../common/constant/constant";
import { toast } from "react-toastify";
import { logout } from "../slice/user.slice";
import { setToast } from "../slice/toast.slice";

/**
 * Base query with automatic token injection
 */
const baseQuery = fetchBaseQuery({
  baseUrl: CONFIG.ADMIN_API_URL,
  // the session is an HttpOnly cookie set by the server, so no token is read or sent from here
  credentials: "include",
});

/**
 * Base query with centralized error handling wrapper
 * Handles all common error scenarios:
 * - 401 Unauthorized: Auto-logout
 * - Other errors: Toast notifications
 */
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  
  if (result.error) {
    const { status, data } = result.error;
    
    // Handle 401 Unauthorized - Auto logout
    if (status === 401) {
      const errorMessage = data?.message || "Session expired. Please login again.";
      toast.error(errorMessage, { autoClose: 2000 });
      
      // Dispatch toast for custom toast component
      api.dispatch(
        setToast({
          message: errorMessage,
          success: false,
          error: true,
        })
      );
      
      // Auto-logout after delay
      setTimeout(() => {
        api.dispatch(logout());
      }, 2000);
      
      return result;
    }
    
    // Handle other errors
    const errorMessage = data?.message || "An error occurred";
    toast.error(errorMessage, { autoClose: 3000 });
    
    // Dispatch toast for custom toast component
    api.dispatch(
      setToast({
        message: errorMessage,
        success: false,
        error: true,
      })
    );
  }
  
  return result;
};

/**
 * Base API instance with centralized error handling
 * All API services should extend from this
 */
export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Vender", "Coin", "Fiat", "CMCKey", "Plan", "Alert", "ThirdPartyKey"],
  endpoints: () => ({}),
});

/**
 * Helper function to create API services that extend the base API
 * This eliminates the need for duplicate error handling in each service
 * 
 * @param {string} reducerPath - Unique path for the API service
 * @param {Function} endpoints - Function that returns endpoint definitions
 * @returns {Object} API service with hooks
 */
export const createApiService = (reducerPath, endpoints) => {
  return baseApi.injectEndpoints({
    name: reducerPath,
    endpoints,
  });
};

